import { useState } from 'react';
import type { Artifact, ArtifactDemand, DemandUnassignedGroup } from '../types.ts';
import {
  UNASSIGNED_GROUP_CAP,
  assignmentTotals,
  capList,
  formatDateRange,
  orderUnassigned,
  splitLane,
  summariseCoverage,
  voicesLabel,
} from '../lib/demand.ts';
import { DemandQuoteList } from './DemandQuoteList.tsx';
import { DemandCoverage, SearchList } from './DemandCoverage.tsx';
import { DemandNotGathered } from './AxisDemandDetail.tsx';
import { Highlight } from './Highlight.tsx';

/**
 * The artifact page's demand sections, after "Not compared": the demand the assigner could not
 * place, then the denominator behind every demand verdict. A run with no demand pass says so
 * once, here — silence would read as "none found", which is a claim the run never made.
 */
export function DemandPageSections({
  artifact,
  highlightQuery = '',
}: {
  artifact: Artifact;
  highlightQuery?: string;
}): JSX.Element {
  if (!artifact.demand) {
    return (
      <section className="artifact-section">
        <h3 className="tick">
          <span className="label">§ Demand</span>
        </h3>
        <div className="panel demand-not-gathered-panel">
          <DemandNotGathered />
        </div>
      </section>
    );
  }
  return (
    <>
      <UnassignedDemand demand={artifact.demand} highlightQuery={highlightQuery} />
      <DemandWhereWeLooked demand={artifact.demand} />
    </>
  );
}

/**
 * "§ Other pain points found" — the unassigned pile: demand the assigner read and could not join
 * to any matrix axis, grouped by the capability area the thread was about. Most voices first. Real
 * runs carry hundreds of one-voice groups, so the list is capped like the quotes are (display
 * only) and a group's quotes are not rendered until it is opened.
 *
 * Signals the assignment pass never reached are NOT in the pile. When there are any, the section
 * opens with their count, in plain words (design-system.md, "Unread signals"): the pile must
 * never read as a complete account of what was set aside.
 */
export function UnassignedDemand({
  demand,
  highlightQuery = '',
}: {
  demand: ArtifactDemand;
  highlightQuery?: string;
}): JSX.Element {
  const [showAll, setShowAll] = useState(false);
  const groups = orderUnassigned(demand.unassigned);
  const { shown, hidden } = capList(groups, showAll, UNASSIGNED_GROUP_CAP);
  const totals = assignmentTotals(demand);

  return (
    <section className="artifact-section">
      <h3 className="tick">
        <span className="label">§ Other pain points found · demand that matched no compared capability</span>
      </h3>
      {totals && totals.unread > 0 && (
        <p className="demand-unread muted">
          {totals.unread} of {totals.signals} signals unread · no assignment batch reached them · they are
          counted nowhere on this page, and they are not in this pile
        </p>
      )}
      {groups.length === 0 ? (
        <p className="panel empty-note muted">every demand quote we kept was joined to a capability or a vendor</p>
      ) : (
        <div className="panel unassigned-demand">
          {shown.map((g, i) => (
            <UnassignedGroup key={`${g.capability_area}::${i}`} group={g} highlightQuery={highlightQuery} />
          ))}
          {(hidden > 0 || showAll) && groups.length > UNASSIGNED_GROUP_CAP && (
            <div className="expand-all-row unassigned-more">
              <button type="button" onClick={() => setShowAll((s) => !s)}>
                {showAll ? `show first ${UNASSIGNED_GROUP_CAP}` : `show all ${groups.length}`}
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function UnassignedGroup({
  group,
  highlightQuery,
}: {
  group: DemandUnassignedGroup;
  highlightQuery: string;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const quotes = group.quotes ?? [];
  return (
    <div className="feature-expansion">
      <button type="button" className="feature-expansion-toggle" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <span className="disclosure" aria-hidden="true">{open ? '▾' : '▸'}</span>{' '}
        <Highlight text={group.capability_area} query={highlightQuery} />
        <span className="sub-count muted">
          {' '}
          {voicesLabel(group.voices ?? 0)} · {quotes.length} {quotes.length === 1 ? 'quote' : 'quotes'}
        </span>
      </button>
      {open && (
        <div className="feature-expansion-body">
          <DemandQuoteList quotes={quotes} highlightQuery={highlightQuery} />
        </div>
      )}
    </div>
  );
}

/**
 * "§ Where we looked" — the run-wide demand denominator: totals, then every lane with its own
 * figures and queries. A lane that fetched nothing is listed like any other; an empty lane is
 * exactly the fact a reader needs before trusting a `no_signal`.
 */
export function DemandWhereWeLooked({ demand }: { demand: ArtifactDemand }): JSX.Element {
  const lanes = demand.coverage ?? [];
  const totals = assignmentTotals(demand);
  return (
    <section className="artifact-section">
      <h3 className="tick">
        <span className="label">§ Where we looked · the searches behind every demand verdict</span>
      </h3>
      <div className="panel demand-where">
        <DemandCoverage summary={summariseCoverage(lanes)} scope={`${lanes.length} ${lanes.length === 1 ? 'lane' : 'lanes'}`} />
        {totals && (
          // the second denominator: of what the lanes found, how much the assignment pass read.
          // Zero unread is stated, not implied — a coverage claim the reader can check.
          <p className="demand-coverage-line demand-assignment-line">
            assignment · {totals.read} of {totals.signals} {totals.signals === 1 ? 'signal' : 'signals'} read ·{' '}
            {totals.unread} unread
          </p>
        )}
        {lanes.length > 0 && (
          <details className="demand-lanes">
            <summary>per lane</summary>
            <div className="scroll-x">
              <table className="demand-lane-table">
                <thead>
                  <tr>
                    <th><span className="label">Lane</span></th>
                    <th><span className="label">Searches</span></th>
                    <th><span className="label">Fetched</span></th>
                    <th><span className="label">Kept</span></th>
                    <th><span className="label">Dates seen</span></th>
                  </tr>
                </thead>
                <tbody>
                  {lanes.map((lane, i) => {
                    const { kind, name } = splitLane(lane.lane);
                    const searches = lane.searches ?? [];
                    return (
                      <tr key={`${lane.lane}::${i}`}>
                        <th>
                          <span className="demand-lane-kind">{kind}</span> {name || <span className="muted">(unnamed)</span>}
                          {searches.length > 0 && <SearchList searches={searches} />}
                        </th>
                        <td>{searches.length}</td>
                        <td>{lane.threads_fetched}</td>
                        <td>{lane.threads_kept}</td>
                        <td>{formatDateRange(lane.date_range_seen) ?? '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </details>
        )}
      </div>
    </section>
  );
}
